/* eslint-disable camelcase */
/**
 * @author Daniel Aguirre
 * @file index.jsx
 * @description Scheduled Appointments View Page.
 */

// Dependencies
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

// Helpers
import AppCardLayout from 'components/AppCardLayout';
import ButtonFilled from 'components/ButtonFilled';
import { VideoCameraFilled } from '@ant-design/icons';
import { hasActiveVideoCall } from 'commons/helpers/utils';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';
import ManagementViewGenerator from 'components/ManagementViewGenerator';

/**
 * Scheduled Appointments View
 * @param { Object } patientActions - patient Actions.
 * @return { Node } React node.
 */
const ScheduledAppointmentsView = ({ patientActions, clinicId }) => {
  const { getUpcomingVideoCalls } = patientActions;
  const [dataSource, setDataSource] = useState([]);
  const history = useHistory();
  const { search } = useLocation();
  const hideContent = search.includes('?redirect=allow');

  const columns = [
    {
      title: 'Acciones',
      dataIndex: 'actions',
      ellipsis: true
    },
    {
      title: 'Nombres',
      dataIndex: 'nombre',
      ellipsis: true
    },
    {
      title: 'Apellidos',
      dataIndex: 'apellidos',
      ellipsis: true
    },
    {
      title: 'Documento',
      dataIndex: 'documento',
      ellipsis: true
    },
    {
      title: 'Tipo de documento',
      dataIndex: 'tipo_doc',
      ellipsis: true
    },
    {
      title: 'Edad',
      dataIndex: 'edad',
      ellipsis: true
    },
    {
      title: 'Fecha y hora',
      dataIndex: 'video_call_date',
      ellipsis: true
    },
    {
      title: 'Detalle',
      dataIndex: 'description',
      ellipsis: true
    }
  ];

  const onOpenVideoCall = (meetingId) => {
    history.push('/main/videocalls-frame', { meetingId });
  };

  const isButtonEnabled = (callInfo) => {
    const videoCallDate = callInfo?.video_call_date;
    if (!videoCallDate) return false;
    const current = moment.utc(videoCallDate.replace('+00:00', 'Z'));
    const time = {
      cap_fecha: current.format('YYYY-MM-DD'),
      cap_hora: current.format('h:mm a')
    };
    return hasActiveVideoCall(time);
  };

  const onGetData = (_, resp) => {
    if (resp?.data) {
      const adapter = resp.data.map((item) => {
        const buttonEnabled = isButtonEnabled(item);
        const actions = (
          <ButtonFilled
            disabled={!buttonEnabled}
            onActionClick={() => onOpenVideoCall(item.id_zoom_call)}
            type={buttonEnabled ? 'green' : 'disabled'}
            textButton="ingresar"
            className="w-full text-center"
            iconComponent={<VideoCameraFilled style={{ marginTop: -2 }} />}
          />
        );
        return {
          actions,
          ...item,
          video_call_date: item?.video_call_date
            ? moment.utc(item.video_call_date).format('DD-MM-YYYY h:mm a')
            : ''
        };
      });
      setDataSource(adapter);
    }
  };

  useEffect(async () => {
    const data = {
      clinicId,
      limit: 1000,
      offset: 1
    };
    if (clinicId !== null) getUpcomingVideoCalls(data, onGetData);
  }, [clinicId]);

  return (
    <AppCardLayout>
      {hideContent ? (
        <></>
      ) : (
        <div className="w-full h-full">
          <ManagementViewGenerator
            columns={columns}
            data={dataSource}
            tableHeader={false}
          />
        </div>
      )}
    </AppCardLayout>
  );
};

/**
 * Component definition of proptypes.
 */
ScheduledAppointmentsView.propTypes = {
  patientActions: PropTypes.shape({
    getUpcomingVideoCalls: PropTypes.func.isRequired
  }).isRequired,
  clinicId: PropTypes.any.isRequired
};
export default ScheduledAppointmentsView;
